import { useEffect, useState } from "react";
import { fetchPdfBlob } from "@/lib/printPdf";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { SealCheck, SealWarning, ArrowSquareOut } from "@phosphor-icons/react";

const CHECKS = [
  ["signed", "Unterschrift(en) aller Mieter vorhanden"],
  ["dated", "Datum eingetragen"],
  ["readable", "Vollständig und gut lesbar"],
  ["match", "Gehört zu dieser Mieterhöhung (Az./Mietvertrag/Beträge)"],
];
const REASONS = ["nicht unterschrieben", "nicht von allen Mietern unterschrieben", "unleserlich/unvollständig", "falsches Formular", "Änderungen/Vorbehalte vermerkt"];

// Prüfung der vom Mieter eingereichten Zustimmung (QR-Upload/Scan) – Vorschau + Checkliste + Entscheidung
export default function ConsentReview({ r, onDecide }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState({});
  const [reason, setReason] = useState("");
  useEffect(() => {
    let u;
    fetchPdfBlob(`/documents/${r.consent_document_id}/download`).then(({ blob }) => { u = URL.createObjectURL(blob); setUrl(u); }).catch((e) => setErr(e.message));
    return () => u && URL.revokeObjectURL(u);
  }, [r.consent_document_id]);
  const allOk = CHECKS.every(([k]) => ok[k]);
  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3 space-y-3" data-testid={`ri-consent-review-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <SealWarning size={22} weight="fill" className="text-amber-600" />
        <div className="font-semibold text-slate-900">Eingereichte Zustimmung prüfen</div>
        <span className="text-xs text-slate-600">eingegangen {formatDate(r.consent_received_at)} · {r.uploads ? `${r.uploads}. Einreichung` : "Upload"}</span>
        {url && <a href={url} target="_blank" rel="noreferrer" className="ml-auto text-xs text-teal-700 hover:underline flex items-center gap-1" data-testid={`ri-consent-review-open-${r.id}`}><ArrowSquareOut size={13} /> Groß öffnen</a>}
      </div>
      <div className="grid md:grid-cols-[1fr_280px] gap-3">
        <div className="rounded border border-slate-200 bg-white h-[420px] overflow-hidden">
          {url ? <iframe title="Eingereichte Zustimmung" src={url} className="w-full h-full" data-testid={`ri-consent-review-preview-${r.id}`} />
            : <div className="h-full flex items-center justify-center text-sm text-slate-500">{err || "Lade Vorschau…"}</div>}
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            {CHECKS.map(([k, l]) => (
              <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-emerald-600" checked={!!ok[k]} onChange={(e) => setOk({ ...ok, [k]: e.target.checked })} data-testid={`ri-consent-check-${k}-${r.id}`} />
                <span>{l}</span>
              </label>
            ))}
          </div>
          <Button className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={!allOk} onClick={() => onDecide({ valid: true })} data-testid={`ri-req-consent-ok-${r.id}`}>
            <SealCheck size={16} /> Zustimmung gültig – bestätigen
          </Button>
          <div className="border-t border-amber-200 pt-2 space-y-1.5">
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-8 rounded border border-slate-300 bg-white text-sm px-2" data-testid={`ri-consent-reason-${r.id}`}>
              <option value="">Grund für Ablehnung wählen…</option>
              {REASONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <Button variant="outline" className="w-full border-rose-300 text-rose-700 hover:bg-rose-50" disabled={!reason} onClick={() => onDecide({ valid: false, note: reason })} data-testid={`ri-req-consent-bad-${r.id}`}>
              Nicht gültig – Mieter erneut anschreiben
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
